import { liveQuery } from 'dexie';
import { useSyncExternalStore } from 'react';
import { db } from '../data/db';
import { connectionState, subscribeConnection, type ConnectionState } from './connectivity';

export interface SyncStatus {
  connection: ConnectionState;
  syncing: boolean;
  /** Messages waiting for delivery for the signed-in account. */
  pending: number;
  conflicts: number;
  rejected: number;
  /** Messages that cannot be sent until someone signs in again. */
  paused: number;
  /** Messages from other accounts on this device, delivered when they next sign in. */
  otherAccounts: number;
}

let owner = '';
let sessionPaused = false;
let syncing = false;
let counts = { pending: 0, conflicts: 0, rejected: 0, otherAccounts: 0 };
let snapshot: SyncStatus = build();
const listeners = new Set<() => void>();

function build(): SyncStatus {
  return { connection: connectionState(), syncing, pending: counts.pending, conflicts: counts.conflicts, rejected: counts.rejected, paused: sessionPaused ? counts.pending : 0, otherAccounts: counts.otherAccounts };
}

function emit() {
  const next = build();
  if (JSON.stringify(next) === JSON.stringify(snapshot)) return;
  snapshot = next;
  listeners.forEach((listener) => listener());
}

let subscription: { unsubscribe(): void } | null = null;
function watchOutbox() {
  subscription?.unsubscribe();
  subscription = liveQuery(() => db.outbox.toArray()).subscribe({
    next(ops) {
      const mine = ops.filter((op) => op.ownerId === owner);
      counts = {
        pending: mine.filter((op) => op.status === 'pending').length,
        conflicts: mine.filter((op) => op.status === 'conflict').length,
        rejected: mine.filter((op) => op.status === 'rejected').length,
        otherAccounts: ops.length - mine.length
      };
      emit();
    },
    error(error) { console.error('Outbox watch failed', error); }
  });
}

export function setStatusOwner(id: string) { owner = id; watchOutbox(); }
export function setSessionPaused(paused: boolean) { sessionPaused = paused; emit(); }
export function setSyncActivity(active: boolean) { syncing = active; emit(); }

subscribeConnection(emit);

export function subscribeStatus(listener: () => void) {
  if (!subscription) watchOutbox();
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
export const getStatus = () => snapshot;

export function useSyncStatus(): SyncStatus {
  return useSyncExternalStore(subscribeStatus, getStatus, getStatus);
}

export function statusLabel(status: SyncStatus): string {
  const changes = (count: number, word = 'change') => `${count} ${word}${count === 1 ? '' : 's'}`;
  if (status.conflicts) return `${changes(status.conflicts)} need${status.conflicts === 1 ? 's' : ''} review.`;
  if (status.rejected) return `${changes(status.rejected)} could not be saved. Review needed.`;
  if (status.paused) return `${changes(status.paused)} need${status.paused === 1 ? 's' : ''} sign-in to sync.`;
  if (status.connection === 'offline') return status.pending ? `Offline. ${changes(status.pending, 'field change')} stored on this device, waiting for a connection.` : 'Offline. Field changes are stored on this device.';
  if (status.pending) return `${changes(status.pending, 'field change')} waiting to sync.`;
  return 'Online. Field changes are synced.';
}
